import PropTypes from "prop-types";
import { useMemo, useEffect, useState, useRef, memo } from "react";
import { Input, Button, Alert, Table, Checkbox } from "antd";
import {
  TeamOutlined,
  PhoneOutlined,
  HomeOutlined,
  IdcardOutlined,
  EnvironmentOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import styles from "../CreateSMS.module.scss";
import { RECIPIENT_EMPTY_VALUE } from "../smsWizardUtils";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 750);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return isMobile;
};

const ResponsiveStrategy = ({ Mobile, Desktop, ...rest }) => {
  const isMobile = useIsMobile();
  return isMobile ? <Mobile {...rest} /> : <Desktop {...rest} />;
};

const ANT_TABLE_ROW_SELECTOR = ".ant-table-row";
const ANT_TABLE_HEAD_SELECTOR = ".ant-table-thead";

const px = (value) => parseFloat(value) || 0;

// scroll.y caps the table body at as many whole rows as fit on screen. The cap
// is computed only from values that do NOT depend on the body's own height
// (row height, the card's max-height, offsets and fixed elements around the
// table). Measuring the wrapper's clientHeight instead would ratchet:
// filtering shrinks the wrapper, which shrinks scroll.y, which never grows
// back after the filter is cleared.
const useTableBodyHeight = (wrapper) => {
  const [bodyHeight, setBodyHeight] = useState(null);
  const rowHeightRef = useRef(0);

  useEffect(() => {
    if (!wrapper) return undefined;

    const measure = () => {
      rowHeightRef.current =
        wrapper.querySelector(ANT_TABLE_ROW_SELECTOR)?.offsetHeight || rowHeightRef.current;
      const rowHeight = rowHeightRef.current;
      if (!rowHeight) {
        setBodyHeight(null);
        return;
      }

      // Header plus everything below the table (error block, footer) stays visible.
      let reserved = wrapper.querySelector(ANT_TABLE_HEAD_SELECTOR)?.offsetHeight ?? 0;
      for (let el = wrapper.nextElementSibling; el; el = el.nextElementSibling) {
        const cs = getComputedStyle(el);
        reserved += el.offsetHeight + px(cs.marginTop) + px(cs.marginBottom);
      }

      // The scrollable card is capped at calc(100vh - Npx); the table may take
      // whatever is left of that cap below the wrapper's top edge.
      let card = wrapper.parentElement;
      while (card && !/auto|scroll/.test(getComputedStyle(card).overflowY)) {
        card = card.parentElement;
      }
      const cardMaxHeight = card ? parseFloat(getComputedStyle(card).maxHeight) : NaN;

      const top = wrapper.getBoundingClientRect().top;
      const available = Number.isFinite(cardMaxHeight)
        ? cardMaxHeight -
          (top - card.getBoundingClientRect().top + card.scrollTop) -
          px(getComputedStyle(card).paddingBottom) -
          reserved
        : window.innerHeight - top - reserved;

      const visibleRows = Math.max(1, Math.floor(available / rowHeight));
      setBodyHeight(visibleRows * rowHeight);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(wrapper);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(wrapper, { childList: true, subtree: true });
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [wrapper]);

  return bodyHeight;
};

const dash = RECIPIENT_EMPTY_VALUE;

const isDash = (v) => v == null || v === "" || v === dash;

const cleanNamePart = (v) => (isDash(v) ? "" : String(v).trim());

const compareNamePart = (rawA, rawB) => {
  const a = cleanNamePart(rawA);
  const b = cleanNamePart(rawB);
  if (!a && b) return 1;
  if (a && !b) return -1;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
};

const recipientColumns = [
  {
    title: "Last Name",
    dataIndex: "lastName",
    key: "lastName",
    ellipsis: true,
    sorter: (a, b) => {
      const lastCompare = compareNamePart(a.lastName, b.lastName);
      if (lastCompare !== 0) return lastCompare;
      return compareNamePart(a.firstName, b.firstName);
    },
  },
  {
    title: "First Name",
    dataIndex: "firstName",
    key: "firstName",
    ellipsis: true,
    sorter: (a, b) => compareNamePart(a.firstName, b.firstName),
  },
  {
    title: "Cell Number",
    dataIndex: "phoneNumber",
    key: "phoneNumber",
    ellipsis: true,
  },
  {
    title: "Account Number",
    dataIndex: "residentKey",
    key: "residentKey",
    ellipsis: true,
    width: 140,
    render: (v) => (v && v !== "—" ? v : "-"),
  },
  {
    title: "Address",
    dataIndex: "address",
    key: "address",
    ellipsis: true,
  },
  {
    title: "Block",
    dataIndex: "block",
    key: "block",
    ellipsis: true,
    width: 100,
  },
  {
    title: "Lot",
    dataIndex: "lot",
    key: "lot",
    ellipsis: true,
    width: 100,
  },
];

const getRecipientDisplayName = (row) => {
  const first = isDash(row.firstName) ? "" : String(row.firstName).trim();
  const last = isDash(row.lastName) ? "" : String(row.lastName).trim();
  const name = `${first} ${last}`.trim();
  return name || dash;
};

const formatBlockLotLine = (row) => {
  const parts = [];
  if (!isDash(row.block)) parts.push(`Block ${row.block}`);
  if (!isDash(row.lot)) parts.push(`Lot ${row.lot}`);
  return parts.length ? parts.join(" · ") : null;
};

const RecipientDesktopView = memo(
  ({
    filteredRecipientRows,
    recipientRows,
    selectedRecipientKeys,
    onRecipientSelectionChange,
    tableBodyHeight,
  }) => {
    const handleChange = (newKeys) => {
      const filteredKeySet = new Set(filteredRecipientRows.map((r) => String(r.key)));
      const outsideFilter = selectedRecipientKeys.filter((k) => !filteredKeySet.has(String(k)));
      onRecipientSelectionChange([...outsideFilter, ...newKeys.map((k) => String(k))]);
    };

    return (
      <div className={styles.recipientTableWrap}>
        <Table
          size="middle"
          rowSelection={{
            selectedRowKeys: selectedRecipientKeys,
            onChange: handleChange,
            getCheckboxProps: () => ({ disabled: recipientRows.length === 0 }),
          }}
          columns={recipientColumns}
          dataSource={filteredRecipientRows}
          pagination={false}
          scroll={{ x: "max-content", y: tableBodyHeight || undefined }}
          locale={{
            emptyText:
              recipientRows.length === 0
                ? "No rows to display"
                : "No recipients match your search",
          }}
        />
      </div>
    );
  }
);

RecipientDesktopView.displayName = "RecipientDesktopView";

const RecipientMobileView = memo(
  ({ filteredRecipientRows, recipientRows, selectedRecipientKeys, onRecipientSelectionChange }) => {
    const selectedSet = useMemo(
      () => new Set(selectedRecipientKeys.map((k) => String(k))),
      [selectedRecipientKeys]
    );

    const toggleKey = (key) => {
      const keyStr = String(key);
      const asStrings = selectedRecipientKeys.map((k) => String(k));
      if (asStrings.includes(keyStr)) {
        onRecipientSelectionChange(asStrings.filter((k) => k !== keyStr));
      } else {
        onRecipientSelectionChange([...asStrings, keyStr]);
      }
    };

    if (!filteredRecipientRows.length) {
      return (
        <div className={styles.step2MobileEmpty}>
          {recipientRows.length === 0
            ? "No rows to display"
            : "No recipients match your search"}
        </div>
      );
    }

    return (
      <div className={styles.step2RecipientsMobile}>
        {filteredRecipientRows.map((row) => {
          const selected = selectedSet.has(String(row.key));
          const blockLot = formatBlockLotLine(row);

          return (
            <div
              key={row.key}
              role="button"
              tabIndex={0}
              onClick={() => toggleKey(row.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleKey(row.key);
                }
              }}
              className={`${styles.step2MobileRecipientCard} ${
                selected ? styles.step2MobileRecipientCardSelected : ""
              }`}
            >
              <div className={styles.step2MobileRecipientInner}>
                <div
                  className={styles.step2MobileRecipientCheckbox}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selected}
                    disabled={recipientRows.length === 0}
                    onChange={() => toggleKey(row.key)}
                  />
                </div>
                <div className={styles.step2MobileRecipientBody}>
                  <h4 className={styles.step2MobileRecipientName}>
                    {getRecipientDisplayName(row)}
                  </h4>
                  <div className={styles.step2MobileRecipientDetails}>
                    <div className={styles.step2MobileDetailRow}>
                      <PhoneOutlined className={styles.step2MobileDetailIcon} />
                      <span className={styles.step2MobileDetailText}>
                        {isDash(row.phoneNumber) ? dash : row.phoneNumber}
                      </span>
                    </div>
                    {!isDash(row.address) && (
                      <div className={styles.step2MobileDetailRow}>
                        <HomeOutlined className={styles.step2MobileDetailIcon} />
                        <span className={styles.step2MobileDetailText}>{row.address}</span>
                      </div>
                    )}
                    {blockLot && (
                      <div className={styles.step2MobileDetailRow}>
                        <EnvironmentOutlined className={styles.step2MobileDetailIcon} />
                        <span className={styles.step2MobileDetailText}>{blockLot}</span>
                      </div>
                    )}
                    {!isDash(row.residentKey) && (
                      <div className={styles.step2MobileDetailRow}>
                        <IdcardOutlined className={styles.step2MobileDetailIcon} />
                        <span className={styles.step2MobileDetailText}>{row.residentKey}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);

RecipientMobileView.displayName = "RecipientMobileView";

const CreateSMSStep2 = ({
  summaryError,
  summaryLoading,
  recipientRows,
  recipientSearch,
  onRecipientSearchChange,
  selectedRecipientKeys,
  onRecipientSelectionChange,
  step2SubmitAttempted,
  onBack,
  onCancel,
  onReviewAndSend,
}) => {
  const [tableScrollNode, setTableScrollNode] = useState(null);
  const tableBodyHeight = useTableBodyHeight(tableScrollNode);

  const filteredRecipientRows = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    if (!q) return recipientRows;
    return recipientRows.filter((r) =>
      [
        r.firstName,
        r.lastName,
        r.phoneNumber,
        r.residentKey,
        r.residentContactKey,
        r.block,
        r.lot,
        r.address,
      ].some((field) =>
        String(field ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }, [recipientRows, recipientSearch]);

  const sharedRecipientProps = {
    filteredRecipientRows,
    recipientRows,
    selectedRecipientKeys,
    onRecipientSelectionChange,
    tableBodyHeight,
  };

  const selectedKeySet = useMemo(
    () => new Set(selectedRecipientKeys.map((k) => String(k))),
    [selectedRecipientKeys]
  );

  const allFilteredSelected =
    filteredRecipientRows.length > 0 &&
    filteredRecipientRows.every((r) => selectedKeySet.has(String(r.key)));

  const handleMobileSelectAllVisible = () => {
    const filteredKeySet = new Set(filteredRecipientRows.map((r) => String(r.key)));
    const outsideFilter = selectedRecipientKeys.filter((k) => !filteredKeySet.has(String(k)));
    const allFiltered = filteredRecipientRows.map((r) => String(r.key));
    onRecipientSelectionChange([...outsideFilter, ...allFiltered]);
  };

  const handleMobileClearAll = () => {
    onRecipientSelectionChange([]);
  };

  return (
    <div className={styles.step2}>
      <div className={styles.step2Header}>
        <h3 className={styles.step2Title}>Filter Recipients</h3>

        {summaryError && (
          <Alert type="error" message={summaryError} showIcon style={{ marginBottom: 16 }} />
        )}

        <Input
          data-testid="sms-recipient-search-input"
          placeholder="Search by name, phone, account number, block, lot, or address..."
          allowClear
          value={recipientSearch}
          onChange={(e) => onRecipientSearchChange(e.target.value)}
          className={styles.recipientSearch}
          suffix={<SearchOutlined style={{ color: "#9ca3af" }} />}
        />

        <div className={styles.recipientToolbar}>
          <div className={styles.recipientToolbarLeft}>
            <TeamOutlined className={styles.recipientToolbarIcon} />
            <span className={styles.recipientToolbarCount}>
              {selectedRecipientKeys.length} of {recipientRows.length} selected
            </span>
          </div>
          {recipientRows.length > 0 && (
            <div className={styles.recipientToolbarRight}>
              <Button
                data-testid="sms-select-all-btn"
                type="link"
                size="small"
                className={styles.recipientToolbarAction}
                disabled={filteredRecipientRows.length === 0 || allFilteredSelected}
                onClick={handleMobileSelectAllVisible}
              >
                Select All
              </Button>
              <Button
                data-testid="sms-clear-all-btn"
                type="link"
                size="small"
                className={styles.recipientToolbarAction}
                disabled={selectedRecipientKeys.length === 0}
                onClick={handleMobileClearAll}
              >
                Clear All
              </Button>
            </div>
          )}
        </div>

        {recipientRows.length === 0 && !summaryLoading && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="No eligible recipients were found for the selected communities."
          />
        )}
      </div>

      <div className={styles.step2TableScroll} ref={setTableScrollNode}>
        <ResponsiveStrategy
          Mobile={RecipientMobileView}
          Desktop={RecipientDesktopView}
          {...sharedRecipientProps}
        />
      </div>

      <div className={styles.step2FooterBlock}>
        {step2SubmitAttempted && recipientRows.length > 0 && selectedRecipientKeys.length === 0 && (
          <div className={styles.errorMsg}>Select at least one recipient.</div>
        )}
        {step2SubmitAttempted && recipientRows.length === 0 && (
          <div className={styles.errorMsg}>No recipients available to send.</div>
        )}
      </div>

      <div className={styles.step2Footer}>
        <Button data-testid="sms-back-btn" onClick={onBack}>Back</Button>
        <div className={styles.step2FooterRight}>
          <Button data-testid="sms-cancel-step2-btn" onClick={onCancel}>Cancel</Button>
          <Button data-testid="sms-review-send-btn" type="primary" onClick={onReviewAndSend}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

CreateSMSStep2.propTypes = {
  summaryError: PropTypes.string,
  summaryLoading: PropTypes.bool.isRequired,
  recipientRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  recipientSearch: PropTypes.string.isRequired,
  onRecipientSearchChange: PropTypes.func.isRequired,
  selectedRecipientKeys: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired,
  onRecipientSelectionChange: PropTypes.func.isRequired,
  step2SubmitAttempted: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onReviewAndSend: PropTypes.func.isRequired,
};

export default CreateSMSStep2;
